import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { OffHoursBanner } from '@flipster/features/src/shared/trading-suspension/off-hours-banner';
import { FormattedMessage } from '@flipster/i18n';
import { CountdownDate } from '@flipster/react/design-utils/countdown-date';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import { FundingRatePrecision } from '@flipster/utils/constants/currency';

import { StatDefinitionGrid, StatDefinitionItem } from '../stat-definition-grid';
import { tradingParameterBunja } from './state';

/** 심볼 종류에 따라 감출 수 있는 파라미터 항목. isHide면 그리드에서 제외된다. */
type TradingParameterItem = StatDefinitionItem & { isHide?: boolean };

/**
 * Trading parameter 패널 (UI 레이어).
 * 값 계산은 tradingParameterBunja(데이터 레이어)에서 하고, 여기선 atom을 consume해 표시/포맷팅만 한다.
 */
const TradingParameter = () => {
  const {
    isSpotAtom,
    positionUnitAtom,
    quoteCurrencyAtom,
    fundingRateAtom,
    fundingTimeAtom,
    fundingIntervalTextAtom,
    fundingIntervalTimezoneAtom,
    tickSizeAtom,
    minTradeAmountAtom,
    tradeQtyPrecisionAtom,
    maxNotionalOrderAmountAtom,
    maxNotionalPositionSizeAtom,
    minNotionalOrderAmountAtom,
    priceProtectionThresholdAtom,
    crossMaxAtom,
    effectiveIsolatedMaxAtom,
    ceilingsDifferAtom,
    maxOpenInterestAtom,
  } = useBunja(tradingParameterBunja);

  const isSpot = useAtomValue(isSpotAtom);
  const positionUnit = useAtomValue(positionUnitAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);
  const fundingRate = useAtomValue(fundingRateAtom);
  const fundingTime = useAtomValue(fundingTimeAtom);
  const fundingIntervalText = useAtomValue(fundingIntervalTextAtom);
  const fundingIntervalTimezone = useAtomValue(fundingIntervalTimezoneAtom);
  const tickSize = useAtomValue(tickSizeAtom);
  const minTradeAmount = useAtomValue(minTradeAmountAtom);
  const tradeQtyPrecision = useAtomValue(tradeQtyPrecisionAtom);
  const maxNotionalOrderAmount = useAtomValue(maxNotionalOrderAmountAtom);
  const maxNotionalPositionSize = useAtomValue(maxNotionalPositionSizeAtom);
  const minNotionalOrderAmount = useAtomValue(minNotionalOrderAmountAtom);
  const priceProtectionThreshold = useAtomValue(priceProtectionThresholdAtom);
  const crossMax = useAtomValue(crossMaxAtom);
  const effectiveIsolatedMax = useAtomValue(effectiveIsolatedMaxAtom);
  const showDualMaxLeverage = useAtomValue(ceilingsDifferAtom);
  const maxOpenInterest = useAtomValue(maxOpenInterestAtom);

  // description: 지표 라벨에 붙는 툴팁 설명. Funding rate/interval은 디자인상 툴팁 없음.
  // notional 계열 body 키(Trade.r260702.*_body)는 "in {symbol}" placeholder를 가지므로
  // 실제 quote currency를 주입한다 (단일 quote 하드코딩 금지).
  // funding / position size / leverage / open interest는 spot에 개념이 없어 isHide로 감춘다.
  const items: TradingParameterItem[] = [
    {
      isHide: isSpot,
      label: <FormattedMessage id='Common.r251107.Funding_rate' />,
      value: <Formatted.Percent value={fundingRate} precision={FundingRatePrecision} toFixed isRate />,
      sub: fundingTime && (
        // CountdownDate는 기본 <p>(블록)라 좁은 컬럼에서 줄바꿈된다 → 인라인 + nowrap으로 한 줄 유지
        <span className={css({ whiteSpace: 'nowrap' })}>
          (in <CountdownDate targetDate={fundingTime} css={{ display: 'inline' }} />)
        </span>
      ),
    },
    {
      isHide: isSpot,
      label: <FormattedMessage id='Trade.r260702.Funding_interval' />,
      value: fundingIntervalText ?? '-',
      // 타임존(UTC)은 데이터 레이어에서 분리 제공 — Realtime 등 타임존이 없는 경우엔 undefined라 표기 안 됨.
      sub: fundingIntervalTimezone,
    },
    {
      label: <FormattedMessage id='Trade.r260702.Min_price_movement' />,
      value: <Formatted.Number value={tickSize} suffix={quoteCurrency} />,
      description: <FormattedMessage id='Trade.r260702.Min_price_movement_body' />,
    },
    {
      label: <FormattedMessage id='Trade.r260702.Min_trade_amount' />,
      value: <Formatted.Number value={minTradeAmount} precision={tradeQtyPrecision} suffix={positionUnit} />,
      description: <FormattedMessage id='Trade.r260702.Min_trade_amount_body' />,
    },
    {
      label: <FormattedMessage id='Trade.r260702.Max_notional_order_amount' />,
      value: <Formatted.Number value={maxNotionalOrderAmount} suffix={quoteCurrency} />,
      description: (
        <FormattedMessage id='Trade.r260702.Max_notional_order_amount_body' values={{ symbol: quoteCurrency }} />
      ),
    },
    {
      isHide: isSpot,
      label: <FormattedMessage id='Trade.r260702.Max_notional_position_size' />,
      value: <Formatted.Number value={maxNotionalPositionSize} suffix={quoteCurrency} />,
      description: (
        <FormattedMessage id='Trade.r260702.Max_notional_position_size_body' values={{ symbol: quoteCurrency }} />
      ),
    },
    {
      label: <FormattedMessage id='Trade.r260702.Min_notional_order_amount' />,
      value: <Formatted.Number value={minNotionalOrderAmount} suffix={quoteCurrency} />,
      description: (
        <FormattedMessage id='Trade.r260702.Min_notional_order_amount_body' values={{ symbol: quoteCurrency }} />
      ),
    },
    {
      label: <FormattedMessage id='Trade.r260702.Price_protection_threshold' />,
      value: <Formatted.Percent value={priceProtectionThreshold} precision={2} toFixed isRate />,
      description: <FormattedMessage id='Trade.r260702.Price_protection_threshold_body' />,
    },
    {
      isHide: isSpot,
      label: <FormattedMessage id='Trade.r251212.Max_leverage' />,
      value:
        crossMax == null ? (
          '-'
        ) : showDualMaxLeverage && effectiveIsolatedMax != null ? (
          <div className={vstack({ gap: 2, alignItems: { base: 'flex-end', smallDesktop: 'flex-start' } })}>
            <p>
              <FormattedMessage id='Trade.r251008.Cross' /> {crossMax}x
            </p>
            <p>
              <FormattedMessage id='Trade.r251008.Isolated' /> {effectiveIsolatedMax}x
            </p>
          </div>
        ) : (
          `${crossMax}x`
        ),
      description: <FormattedMessage id='Trade.r260702.Max_leverage_body' />,
    },
    {
      isHide: isSpot,
      label: <FormattedMessage id='Trade.r260702.Max_open_interest' />,
      value: <Formatted.Number value={maxOpenInterest} suffix={quoteCurrency} />,
      description: <FormattedMessage id='Trade.r260707.Max_open_interest_body' />,
    },
  ];

  const visibleItems = items.filter((item) => !item.isHide);

  return (
    <div className={css({ overflowY: 'scroll' })}>
      <div className={vstack({ p: 16, gap: 16, alignItems: 'stretch', color: 'content.default.level-5' })}>
        <StatDefinitionGrid items={visibleItems} dtClassName={css({ color: 'content.default.level-2' })} valueGap={2} />

        <OffHoursBanner />
      </div>
    </div>
  );
};

export default TradingParameter;
